import React, { FC } from 'react';
import { Box, Grid, Paper, Tooltip, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { Vessel } from '../../../types/Vessel';
import { useTranslation } from 'react-i18next';

import { getContainerTotalWeights } from '../../utils/vesselWeightHelpers';
import WeightDistributionBar from './WeightDistributionBar';

interface WeightOverLengthProps {
  vessel: Vessel;
  containerWeights: number[];
  tonnage: number;
}

const WEIGHT_GROUP_FRONT = "front";
const WEIGHT_GROUP_MIDDLE = "middle";
const WEIGHT_GROUP_BACK = "back";

const vesselWeightGroups = (containerWeights: any, weightGroup: any, t:any) => {
  let formattedWeightGroup = [];

  weightGroup.map((group, index) => {
    formattedWeightGroup[group] ??= [];
    formattedWeightGroup[group].push(index);
  });

  if (
    !formattedWeightGroup[WEIGHT_GROUP_FRONT] ||
    !formattedWeightGroup[WEIGHT_GROUP_MIDDLE] ||
    !formattedWeightGroup[WEIGHT_GROUP_BACK]
  ) {
    return "";
  }

  const frontWeights = containerWeights.filter((weight, index) =>
    formattedWeightGroup[WEIGHT_GROUP_FRONT].includes(index)
  );
  const middleWeights = containerWeights.filter((weight, index) =>
    formattedWeightGroup[WEIGHT_GROUP_MIDDLE].includes(index)
  );
  const backWeights = containerWeights.filter((weight, index) =>
    formattedWeightGroup[WEIGHT_GROUP_BACK].includes(index)
  );
  const frontTotalWeight = frontWeights.reduce(
    (accumulator, weight) => accumulator + weight,
    0
  );
  const middleTotalWeights = middleWeights.reduce(
    (accumulator, weight) => accumulator + weight,
    0
  );
  const backTotalWeights = backWeights.reduce(
    (accumulator, weight) => accumulator + weight,
    0
  );

  return (
    <>
      <Grid className="weight-over-beam-content-tonnage" item>
        <Tooltip
          title={`Rows: ${formattedWeightGroup[WEIGHT_GROUP_FRONT].map(
            (layer) => layer + 1
          ).toString()}`}
        >
          <Grid
            className="weight-over-beam-content-tonnage-per-row"
            item
            sx={{ width: "35% !important" }}
          >
            <small>{t("front")}</small>
            <Typography>{(frontTotalWeight / 1000).toFixed(0)}T</Typography>
          </Grid>
        </Tooltip>
        <Tooltip
          title={`Rows: ${formattedWeightGroup[WEIGHT_GROUP_MIDDLE].map(
            (layer) => layer + 1
          ).toString()}`}
        >
          <Grid
            className="weight-over-beam-content-tonnage-per-row"
            item
            sx={{ width: "35% !important" }}
          >
            <small>{t("middle")}</small>
            <Typography>{(middleTotalWeights / 1000).toFixed(0)}T</Typography>
          </Grid>
        </Tooltip>
        <Tooltip
          title={`Rows: ${formattedWeightGroup[WEIGHT_GROUP_BACK].map(
            (layer) => layer + 1
          ).toString()}`}
        >
          <Grid
            className="weight-over-beam-content-tonnage-per-row"
            item
            sx={{ width: "35% !important" }}
          >
            <small>{t("back")}</small>
            <Typography>{(backTotalWeights / 1000).toFixed(0)}T</Typography>
          </Grid>
        </Tooltip>
      </Grid>
    </>
  );
};

const defaultWeights = (containerWeights: any) => {
  if (containerWeights.length === 0 || containerWeights.length < 3) {
    return "";
  }

  let totalContainerWeights = getContainerTotalWeights(containerWeights);

  return (
    <Grid
    item
    className="weight-over-beam-content-wrapper"
    >
        <Grid
            className="weight-over-beam-content"
            item
            sx={{ width: "100% !important" }}
        >
            <Grid className="weight-over-beam-content-tonnage" item>
            {totalContainerWeights.map((containerWeight) => (
                <>
                <Grid
                    className="weight-over-beam-content-tonnage-per-row"
                    item
                    sx={{ width: "35% !important" }}
                >
                    <small>{containerWeight.range}</small>
                    <Typography>
                    {(containerWeight.total / 1000).toFixed(0)}T
                    </Typography>
                </Grid>
                </>
            ))}
            </Grid>
        </Grid>
    </Grid>
  );
};

const WeightOverLength: FC<WeightOverLengthProps> = ({ vessel, containerWeights, tonnage }) => {
  const { t } = useTranslation();
  const borderColor = `1px solid ${lighten('#343A40', 0.7)}`;
  const hasGroups = vessel.weightGroup && vessel.weightGroup.length > 0;

  return (
    <Box component={Paper} border={borderColor} mb={2}>
      <Typography variant="subtitle1" className="vessel-stability-title">
        {t("weightOverLength")}
      </Typography>

      <Grid item>
        {hasGroups
          ? vesselWeightGroups(containerWeights, vessel.weightGroup!, t)
          : defaultWeights(containerWeights)}
      </Grid>

      <Grid container>
        <WeightDistributionBar
          containerWeights={containerWeights}
          weightGroup={vessel.weightGroup || []}
        />
      </Grid>
    </Box>
  );
};

export default WeightOverLength;